/**
 * MED-1355: the hint for a coach who cannot lead a group or be a 1:1 coach
 * yet. Both need a Medley email, so the group and 1:1 pickers and the coach
 * cards show such a coach disabled with this hint: "Add a Medley email
 * first" and an "Add Medley email" link to the coach profile. The cohort pool
 * needs no Medley email, so Add Potential Coaches does not use it.
 *
 * QA round 5: the link opens the profile in the same tab. A second tab is a
 * second copy of the app, and during impersonation that copy can end the
 * session. A picker in a modal passes onNavigate (its onClose), so the modal
 * closes before the page changes.
 *
 * The click stops at the link. A disabled Mantine combobox option calls
 * preventDefault on a click, and that stops the link. The mousedown must
 * reach the option: the option keeps the focus in the Select, so the
 * dropdown stays open until the click.
 */
import type { MouseEvent } from 'react';
import { Anchor, Group, Text, type GroupProps } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';

export const NEEDS_MEDLEY_EMAIL_TEXT = 'Add a Medley email first';

interface NeedsMedleyEmailHintProps {
  coachProfileId: string | null | undefined;
  coachName: string;
  justify?: GroupProps['justify'];
  /** Runs before the page changes, for example to close the modal. */
  onNavigate?: () => void;
}

export function NeedsMedleyEmailHint({
  coachProfileId,
  coachName,
  justify,
  onNavigate,
}: NeedsMedleyEmailHintProps) {
  const navigate = useNavigate();
  const to = coachProfileId ? `/coaches/${coachProfileId}` : null;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    // A modifier click keeps the browser's own choice (for example a new tab).
    if (!to || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onNavigate?.();
    navigate(to);
  };

  return (
    <Group gap={6} wrap="wrap" justify={justify}>
      <Text size="xs" c="var(--medley-text-warning)">
        {NEEDS_MEDLEY_EMAIL_TEXT}
      </Text>
      {to && (
        <Anchor
          component={Link}
          to={to}
          size="xs"
          fw={600}
          c="var(--medley-text-warning)"
          underline="always"
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
          aria-label={`Add Medley email for ${coachName}`}
        >
          Add Medley email
        </Anchor>
      )}
    </Group>
  );
}

export default NeedsMedleyEmailHint;
